import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Minimize2, Pencil } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ImageViewer } from "@/components/ui/image-viewer";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useElementWidth } from "@/hooks/use-element-width";

const clamp = (n: number, min: number, max: number) =>
  Math.min(max, Math.max(min, n));

/**
 * Polaroid ölçüleri kabın genişliğinden türetilir: dar dock'ta küçük ve sık,
 * tam sayfada eski 150/220px'lik yerleşim. Yığın kabı aşmaz.
 */
function getPolaroidLayout(width: number, count: number) {
  const w = width || 400;
  const size = clamp(Math.round(w * 0.34), 96, 150);
  // Yığın toplamı kabı aşarsa aralık daralır (en az 18px, sayaç okunsun)
  const naturalGap = Math.round(size * 0.3);
  const fitGap = count > 1 ? Math.floor((w - size) / (count - 1)) : naturalGap;
  const gap = clamp(Math.min(naturalGap, fitGap), 18, naturalGap);
  const slide = clamp(Math.round(w * 0.55), 140, 220);
  return {
    size,
    gap,
    stackHeight: size + 50,
    slide,
    slideStep: slide + 24, // gap-6
    slideAreaHeight: slide + 60,
  };
}

interface ScatteredImageCarouselProps {
  images: string[];
  messageId: string;
  onEdit?: (messageId: string) => void;
  role?: "user" | "assistant";
}

interface SelectedImage {
  url: string;
  messageId: string;
  index: number;
}

export const ScatteredImageCarousel: React.FC<ScatteredImageCarouselProps> = ({
  images,
  messageId,
  onEdit,
  role = "assistant",
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedImage, setSelectedImage] = useState<SelectedImage | null>(
    null
  );
  const wrapRef = useRef<HTMLDivElement>(null);
  const layout = getPolaroidLayout(useElementWidth(wrapRef), images.length);

  // Scattered mode: Horizontal layout with alternating tilt
  // Use messageId + index as seed for consistent but random-looking placement
  const getScatteredTransform = (index: number) => {
    // Simple hash function for deterministic randomness
    const hash = (str: string) => {
      let hash = 0;
      for (let i = 0; i < str.length; i++) {
        hash = (hash << 5) - hash + str.charCodeAt(i);
        hash = hash & hash;
      }
      return Math.abs(hash);
    };

    const seed = hash(messageId + index.toString());

    // Generate pseudo-random values based on seed
    const random = (min: number, max: number, offset: number) => {
      const value = ((seed + offset) * 9301 + 49297) % 233280;
      return min + (value / 233280) * (max - min);
    };

    const baseX = index * layout.gap; // aralık kap genişliğine göre
    const jitter = layout.size / 150; // küçük kartta sapma da küçülür
    const randomOffsetX = random(-8, 8, index * 5) * jitter;
    const randomOffsetY = random(-12, 12, index * 7) * jitter;

    const baseTilt = index % 2 === 0 ? -6 : 6; // Alternate between -6° and +6°
    const randomTilt = random(-3, 3, index * 3); // Small random variation on top

    return {
      rotate: baseTilt + randomTilt, // Alternating tilt with slight variation
      x: baseX + randomOffsetX, // Side by side with wider spacing
      y: randomOffsetY, // Slight vertical offset
      z: index,
    };
  };

  // Navigation handlers
  const handlePrevious = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const handleImageClick = (index: number) => {
    // Always open full viewer when clicking on image
    setSelectedImage({
      url: images[index],
      messageId,
      index: index,
    });
  };

  const handleToggleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  // User mesajlarında görsel text'ten önce geldiği için mb-2 (alt boşluk)
  // Assistant mesajlarında görsel text'ten sonra geldiği için mt-2 (üst boşluk)
  const marginClass = role === "user" ? "mb-2" : "mt-2";

  return (
    <>
      <div ref={wrapRef} className={cn("relative", marginClass)}>
        {/* Collapsed Mode: Horizontal Scattered Layout */}
        {!isExpanded && (
          <motion.div
            className="relative w-full max-w-full"
            style={{ height: layout.stackHeight }}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
          >
            {images.map((image, index) => {
              const transform = getScatteredTransform(index);
              return (
                <motion.div
                  key={`${messageId}-scattered-${index}`}
                  className="absolute left-0 top-1/2 cursor-pointer"
                  style={{
                    width: layout.size,
                    height: layout.size,
                    zIndex: transform.z,
                  }}
                  initial={{
                    x: 0,
                    y: "-50%",
                    rotate: 0,
                    scale: 0.8,
                  }}
                  animate={{
                    x: transform.x,
                    y: `calc(-50% + ${transform.y}px)`,
                    rotate: transform.rotate,
                    scale: 1,
                  }}
                  whileHover={{
                    scale: 1.05,
                    y: `calc(-50% + ${transform.y}px)`,
                    zIndex: 100,
                    transition: { duration: 0.2 },
                  }}
                  transition={{
                    duration: 0.5,
                    delay: index * 0.1,
                    type: "spring",
                    stiffness: 100,
                  }}
                  onClick={() => handleImageClick(index)}
                >
                  {/* Polaroid Frame — çerçeve payı kartla orantılı */}
                  <div
                    className={cn(
                      "w-full h-full bg-white dark:bg-gray-800 rounded-lg shadow-xl",
                      layout.size < 120 ? "p-2 pb-5" : "p-3 pb-8",
                    )}
                  >
                    <div className="w-full h-full rounded overflow-hidden bg-gray-100 dark:bg-gray-700">
                      <img
                        src={image}
                        alt={`Image ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>

                  {/* Image Counter Badge */}
                  {index === images.length - 1 && (
                    <div className="absolute top-1.5 right-1.5 bg-black/70 text-white text-[10px] px-1.5 py-0.5 rounded-full backdrop-blur-sm">
                      {t("common.photoCount", { count: images.length })}
                    </div>
                  )}
                </motion.div>
              );
            })}
          </motion.div>
        )}

        {/* Expand Button for Collapsed Mode */}
        {!isExpanded && images.length > 1 && (
          <div className="flex flex-col items-start gap-2 mt-4">
            <button
              onClick={() => setIsExpanded(true)}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors flex items-center gap-2 border border-border-primary"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="15 3 21 3 21 9"></polyline>
                <polyline points="9 21 3 21 3 15"></polyline>
                <line x1="21" y1="3" x2="14" y2="10"></line>
                <line x1="3" y1="21" x2="10" y2="14"></line>
              </svg>
              <span>{t("common.expandGallery")}</span>
            </button>
            {/* 🎨 Edit button for collapsed mode */}
            {onEdit && (
              <Button
                variant="outline"
                className="bg-transparent border border-border font-medium shadow-sm rounded-xl"
                onClick={() => onEdit(messageId)}
              >
                <div className="flex items-center justify-between w-full px-4 py-2.5">
                  <div className="flex items-center gap-2">
                    <Pencil className="h-4 w-4 text-foreground" />
                    <span className="text-sm text-foreground font-medium">
                      {t("common.edit")}
                    </span>
                  </div>
                </div>
              </Button>
            )}
          </div>
        )}

        {/* Expanded Mode: Horizontal Polaroid Carousel */}
        {isExpanded && (
          <motion.div
            className="relative w-full max-w-[900px] mx-auto"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
          >
            {/* Carousel Container */}
            <div className="relative bg-transparent rounded-xl p-4">
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="text-sm text-text-tertiary dark:text-gray-400">
                  {currentIndex + 1} / {images.length}
                </div>
                <button
                  onClick={handleToggleExpand}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors flex items-center gap-2 border border-border-primary"
                  aria-label="Collapse carousel"
                >
                  <Minimize2 className="w-4 h-4 text-text-tertiary dark:text-gray-400" />
                  {t("common.collapseGallery")}
                </button>
              </div>

              {/* Horizontal Polaroid Strip - Sliding Carousel */}
              <div
                className="relative overflow-hidden"
                style={{ height: layout.slideAreaHeight }}
              >
                <motion.div
                  className="flex items-center gap-6 h-full"
                  animate={{
                    x: `calc(50% - ${currentIndex * layout.slideStep + layout.slide / 2}px)`,
                  }}
                  transition={{
                    type: "spring",
                    stiffness: 300,
                    damping: 30,
                  }}
                >
                  {images.map((image, index) => {
                    const isActive = index === currentIndex;

                    return (
                      <motion.div
                        key={`${messageId}-expanded-${index}`}
                        className="flex-shrink-0 cursor-pointer"
                        animate={{
                          opacity: isActive ? 1 : 0.6,
                          scale: isActive ? 1.05 : 0.85,
                        }}
                        transition={{ duration: 0.3 }}
                        onClick={() => {
                          if (isActive) {
                            handleImageClick(index);
                          } else {
                            setCurrentIndex(index);
                          }
                        }}
                        style={{
                          width: layout.slide,
                          height: layout.slide,
                          zIndex: isActive ? 10 : 1,
                        }}
                      >
                        {/* Polaroid Frame */}
                        <div
                          className={cn(
                            "w-full h-full bg-white dark:bg-gray-800 rounded-lg shadow-lg p-3 pb-8 transition-all duration-300",
                            isActive
                              ? "ring-4 ring-primary/30"
                              : "hover:shadow-xl"
                          )}
                        >
                          <div className="w-full h-full rounded overflow-hidden bg-gray-100 dark:bg-gray-700">
                            {isActive ? (
                              <motion.img
                                layoutId={`${messageId}-image-${index}`}
                                src={image}
                                alt={`Image ${index + 1}`}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <img
                                src={image}
                                alt={`Image ${index + 1}`}
                                className="w-full h-full object-cover"
                              />
                            )}
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </motion.div>

                {/* Navigation Buttons */}
                {images.length > 1 && (
                  <>
                    <button
                      onClick={handlePrevious}
                      className="absolute left-0 top-1/2 -translate-y-1/2 p-3 bg-black/50 hover:bg-black/70 text-white rounded-full backdrop-blur-sm transition-all z-20"
                      aria-label="Previous image"
                    >
                      <ChevronLeft className="w-6 h-6" />
                    </button>
                    <button
                      onClick={handleNext}
                      className="absolute right-0 top-1/2 -translate-y-1/2 p-3 bg-black/50 hover:bg-black/70 text-white rounded-full backdrop-blur-sm transition-all z-20"
                      aria-label="Next image"
                    >
                      <ChevronRight className="w-6 h-6" />
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* 🎨 Edit button for expanded mode */}
            {onEdit && (
              <div className="mt-4 flex justify-start">
                <Button
                  variant="outline"
                  className="bg-white dark:bg-gray-800 border border-border hover:bg-gray-50 dark:hover:bg-gray-700 font-medium shadow-sm rounded-xl"
                  onClick={() => onEdit(messageId)}
                >
                  <div className="flex items-center justify-between w-full px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <Pencil className="h-4 w-4 text-foreground" />
                      <span className="text-sm text-foreground font-medium">
                        {t("common.edit")}
                      </span>
                    </div>
                  </div>
                </Button>
              </div>
            )}
          </motion.div>
        )}
      </div>

      {/* Full Image Viewer */}
      {selectedImage && (
        <ImageViewer
          image={selectedImage.url}
          messageId={selectedImage.messageId}
          imageIndex={selectedImage.index}
          onClose={() => setSelectedImage(null)}
        />
      )}
    </>
  );
};
